import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  EyeOff,
  Star,
  Pencil,
  Trash2,
  MessageSquareQuote,
  CheckSquare,
  Square as SquareIcon,
} from 'lucide-react';
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from 'recharts';
import {
  getAllReviews,
  getReviewStats,
  toggleVisibility,
  toggleFeatured,
  updateReview,
  deleteReview,
} from '../../api/reviews';
import { showError, showSuccess, confirmDialog } from '../../utils/toast';
import '../Services/ServiceList.css';
import './ReviewsManager.css';

const PAGE_SIZE = 12;

function strHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function avatarHue(name) {
  return 210 + (strHash(name || 'x') % 120);
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0];
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
  return `${(a || '').toUpperCase()}${(b || '').toUpperCase()}`;
}

function starsTxt(n) {
  const num = Number(n) || 0;
  return '★'.repeat(num) + '☆'.repeat(Math.max(0, 5 - num));
}

const rowVariant = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0 },
};

const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filterTab, setFilterTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const s = await getReviewStats();
      setStats(s);
    } catch (e) {
      console.error(e);
      showError('Failed to load review stats');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const acc = [];
      let p = 1;
      let totalPages = 1;
      let safety = 0;
      do {
        const res = await getAllReviews(p, 100);
        const chunk = Array.isArray(res?.data) ? res.data : [];
        acc.push(...chunk);
        totalPages = res?.totalPages || 1;
        p += 1;
        safety += 1;
        if (safety > 200) break;
      } while (p <= totalPages);

      setReviews(acc);
    } catch (e) {
      console.error(e);
      showError('Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
    loadStats();
  }, [loadReviews, loadStats]);

  const filtered = useMemo(() => {
    let list = [...reviews];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          String(r.name || '')
            .toLowerCase()
            .includes(q) ||
          String(r.message || '')
            .toLowerCase()
            .includes(q)
      );
    }
    if (filterTab === 'approved') list = list.filter((r) => Number(r.is_visible) === 1);
    else if (filterTab === 'pending') list = list.filter((r) => Number(r.is_visible) !== 1);
    else if (filterTab === 'featured') list = list.filter((r) => Number(r.is_featured) === 1);
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [reviews, filterTab, search]);

  useEffect(() => {
    setPage(1);
  }, [filterTab, search]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  useEffect(() => {
    setPage((p) => Math.min(Math.max(p, 1), totalPages));
  }, [totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const mergeRow = useCallback((updated) => {
    if (!updated?.id) return;
    setReviews((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }, []);

  const onToggleVisibility = async (rec) => {
    const optimistic = {
      ...rec,
      is_visible: Number(rec.is_visible) === 1 ? 0 : 1,
    };
    mergeRow(optimistic);
    try {
      const row = await toggleVisibility(rec.id);
      mergeRow(row);
      await loadStats();
      showSuccess('Visibility updated');
    } catch (e) {
      console.error(e);
      mergeRow(rec);
      showError('Failed to toggle visibility');
    }
  };

  const onToggleFeatured = async (rec) => {
    const optimistic = {
      ...rec,
      is_featured: Number(rec.is_featured) === 1 ? 0 : 1,
    };
    mergeRow(optimistic);
    try {
      const row = await toggleFeatured(rec.id);
      mergeRow(row);
      await loadStats();
      showSuccess('Featured updated');
    } catch (e) {
      console.error(e);
      mergeRow(rec);
      showError('Failed to update featured flag');
    }
  };

  const onDeleteRow = async (rec) => {
    const ok = await confirmDialog(`Delete review from "${rec.name}"?`);
    if (!ok) return;
    try {
      await deleteReview(rec.id);
      setReviews((prev) => prev.filter((r) => r.id !== rec.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(rec.id);
        return next;
      });
      await loadStats();
      showSuccess('Review deleted');
    } catch (e) {
      console.error(e);
      showError('Failed to delete review');
    }
  };

  const openEdit = (rec) => {
    setEditRow(rec);
    setEditForm({
      name: rec.name || '',
      role: rec.role || '',
      message: rec.message || '',
      rating: String(rec.rating || 5),
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const rating = parseInt(editForm.rating, 10);
    if (!editForm.name?.trim()) {
      showError('Name is required');
      return;
    }
    if (!editForm.message?.trim()) {
      showError('Message is required');
      return;
    }
    if (Number.isNaN(rating) || rating < 1 || rating > 5) {
      showError('Rating must be 1–5');
      return;
    }
    setSavingEdit(true);
    try {
      const row = await updateReview(editRow.id, {
        name: editForm.name.trim(),
        role: editForm.role?.trim() || null,
        message: editForm.message.trim(),
        rating,
      });
      mergeRow(row);
      await loadStats();
      showSuccess('Review updated');
      setEditRow(null);
    } catch (e) {
      console.error(e);
      showError('Failed to save review');
    } finally {
      setSavingEdit(false);
    }
  };

  const bulkApprove = async () => {
    const ids = [...selectedIds].filter((id) => {
      const r = reviews.find((rev) => rev.id === id);
      return r && Number(r.is_visible) !== 1;
    });
    if (ids.length === 0) {
      showError('Select pending reviews to approve');
      return;
    }
    try {
      await Promise.all(
        ids.map(async (id) => {
          const r = reviews.find((x) => x.id === id);
          if (!r || Number(r.is_visible) === 1) return;
          mergeRow({ ...r, is_visible: 1 });
          try {
            const row = await toggleVisibility(id);
            mergeRow(row);
          } catch (e) {
            mergeRow(r);
            throw e;
          }
        })
      );
      await loadStats();
      showSuccess(`${ids.length} review(s) approved`);
    } catch (e) {
      console.error(e);
      showError('Some approvals failed');
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) {
      showError('Nothing selected');
      return;
    }
    const ok = await confirmDialog(`Delete ${selectedIds.size} review(s)?`);
    if (!ok) return;
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map((id) => deleteReview(id)));
      setReviews((prev) => prev.filter((r) => !selectedIds.has(r.id)));
      setSelectedIds(new Set());
      await loadStats();
      showSuccess('Reviews deleted');
    } catch (e) {
      console.error(e);
      showError('Bulk delete failed');
    }
  };

  const avgChartData = useMemo(() => {
    const v = stats?.averageRating != null ? Number(stats.averageRating) : 0;
    const pct = Math.min(100, Math.max(0, (v / 5) * 100));
    return [{ name: 'avg', value: pct, fill: '#8B5CF6' }];
  }, [stats?.averageRating]);

  if (loading && reviews.length === 0) {
    return (
      <div className="service-list-container">
        <div className="loading">Loading reviews…</div>
      </div>
    );
  }

  return (
    <div className="reviews-manager">
      <div className="page-header">
        <h1>Reviews & Testimonials</h1>
      </div>

      <div className="reviews-stats-grid">
        <div className="reviews-stat-card">
          <div className="reviews-stat-label">Total</div>
          <div className="reviews-stat-value">
            {statsLoading ? '—' : stats?.total ?? 0}
          </div>
        </div>
        <div className="reviews-stat-card">
          <div className="reviews-stat-label">Visible</div>
          <div className="reviews-stat-value">
            {statsLoading ? '—' : stats?.visible ?? 0}
          </div>
        </div>
        <div className="reviews-stat-card">
          <div className="reviews-stat-label">Hidden</div>
          <div className="reviews-stat-value">
            {statsLoading ? '—' : stats?.hidden ?? 0}
          </div>
        </div>
        <div className="reviews-stat-card">
          <div className="reviews-stat-label">Avg rating</div>
          <div className="reviews-stat-value" style={{ fontSize: '1.65rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {statsLoading
              ? '—'
              : stats?.averageRating != null
              ? `${stats.averageRating} / 5`
              : '—'}
          </div>
          {!statsLoading && stats?.averageRating != null && (
            <div className="reviews-radial-wrap" aria-hidden>
              <ResponsiveContainer width="100%" height={110}>
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={54}
                  data={avgChartData}
                  barSize={12}
                  startAngle={90}
                  endAngle={-270}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                  <RadialBar background={{ fill: 'var(--bg-secondary)', opacity: 0.65 }} clockWise dataKey="value" cornerRadius={8} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="reviews-controls">
        <div className="reviews-tab-group">
          {['all', 'approved', 'pending', 'featured'].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`reviews-tab ${filterTab === tab ? 'active' : ''}`}
              onClick={() => setFilterTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <div className="reviews-search">
          <input
            type="search"
            placeholder="Search by name or message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="reviews-bulk-actions">
          <button type="button" className="btn btn-primary" onClick={bulkApprove}>
            Bulk approve
          </button>
          <button type="button" className="btn btn-sm btn-delete" onClick={bulkDelete}>
            Bulk delete
          </button>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="empty-reviews-state">
          <MessageSquareQuote size={48} />
          <p>No reviews yet. They will appear here when submitted from your website.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p>No reviews match your filters.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="services-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Reviewer</th>
                  <th>Rating</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Featured</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="sync">
                  {paged.map((rec) => {
                    const vis = Number(rec.is_visible) === 1;
                    const feat = Number(rec.is_featured) === 1;
                    return (
                      <motion.tr
                        key={rec.id}
                        variants={rowVariant}
                        initial="hidden"
                        animate="show"
                        transition={{ duration: 0.2 }}
                        className={`${vis ? '' : 'pending-row'}`}
                      >
                        <td>
                          <button
                            type="button"
                            aria-label={selectedIds.has(rec.id) ? 'Deselect' : 'Select'}
                            className="icon-btn-round"
                            onClick={() => toggleSelected(rec.id)}
                          >
                            {selectedIds.has(rec.id) ? <CheckSquare size={18} /> : <SquareIcon size={18} />}
                          </button>
                        </td>
                        <td>
                          <div className="reviews-meta">
                            <div
                              className="reviews-avatar"
                              style={{ backgroundColor: `hsl(${avatarHue(rec.name)}, 55%, 45%)` }}
                            >
                              {initials(rec.name)}
                            </div>
                            <div>
                              <strong>{rec.name}</strong>
                              {rec.role && (
                                <>
                                  <br />
                                  <span className="text-muted">{rec.role}</span>
                                </>
                              )}
                              <br />
                              <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                                {rec.email ? `${rec.email}` : ''}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="stars-mini">{starsTxt(rec.rating)}</span>
                        </td>
                        <td>
                          <div className="reviews-msg-cell">
                            {(rec.message || '').substring(0, 80)}
                            {(rec.message || '').length > 80 ? '…' : ''}
                          </div>
                        </td>
                        <td>
                          <span className={`badge-soft ${vis ? 'success' : 'muted'}`}>
                            {vis ? 'Visible' : 'Hidden'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge-soft ${feat ? 'amber' : 'muted'}`}>
                            <Star size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            {feat ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <small>
                            {rec.created_at
                              ? new Date(rec.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '—'}
                          </small>
                        </td>
                        <td>
                          <div className="reviews-actions">
                            <button
                              type="button"
                              className="icon-btn-round"
                              title={vis ? 'Hide' : 'Show'}
                              onClick={() => onToggleVisibility(rec)}
                            >
                              {vis ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                            <button
                              type="button"
                              className="icon-btn-round"
                              title={feat ? 'Unfeature' : 'Feature'}
                              onClick={() => onToggleFeatured(rec)}
                            >
                              <Star size={18} className={feat ? 'reviews-thumb-star' : ''} />
                            </button>
                            <button type="button" className="icon-btn-round" title="Edit" onClick={() => openEdit(rec)}>
                              <Pencil size={18} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn-round danger"
                              title="Delete"
                              onClick={() => onDeleteRow(rec)}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          <div className="reviews-pagination">
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Page {page} of {totalPages} ({totalFiltered} shown)
            </span>
            <button
              type="button"
              className="btn btn-sm btn-edit"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-sm btn-edit"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {editRow && (
        <div
          className="edit-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.target === e.currentTarget && setEditRow(null)}
        >
          <div className="edit-modal-panel">
            <h3>Edit review</h3>
            <div className="edit-field">
              <label htmlFor="rw-name">Name</label>
              <input id="rw-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label htmlFor="rw-role">Role</label>
              <input id="rw-role" value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label htmlFor="rw-msg">Message</label>
              <textarea id="rw-msg" value={editForm.message} onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))} />
            </div>
            <div className="edit-field">
              <label htmlFor="rw-r">Rating</label>
              <select id="rw-r" value={editForm.rating} onChange={(e) => setEditForm((f) => ({ ...f, rating: e.target.value }))}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={String(n)}>
                    {n} star{n > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="edit-modal-actions">
              <button type="button" className="btn btn-sm btn-delete" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={savingEdit} onClick={saveEdit}>
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsManager;
